// src/features/auth/ForgotPassword.jsx
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Form, 
  Button, 
  Alert,
  Spinner
} from 'react-bootstrap';
import { 
  Mail, 
  Lock, 
  ArrowLeft,
  CheckCircle,
  XCircle,
  Shield,
  Sparkles,
  Send,
  Key,
  Eye,
  EyeOff
} from 'lucide-react';
import { businessService } from '../../services';
import './ForgotPassword.css';

export default function ForgotPassword() {
  const navigate = useNavigate();
  
  // Step 1: Email
  const [email, setEmail] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');
  
  // Step 2: New Password (Direct reset with token)
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');
  
  // UI States
  const [step, setStep] = useState(1); // 1=Email, 2=Reset Password
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Check for token in URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tokenParam = params.get('token');
    if (tokenParam) {
      setToken(tokenParam);
      setStep(2);
    }
  }, []);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setEmailError('Please enter your email address');
      return;
    }

    setEmailLoading(true);
    setEmailError('');
    setEmailSuccess('');

    try {
      const result = await businessService.forgotPassword(email);
      if (result.success) {
        setEmailSuccess('If an account exists, a password reset link has been sent to your email.');
        // Keep the message but don't auto-advance - user checks email
      } else {
        setEmailError(result.message || 'Failed to send reset link');
      }
    } catch (err) {
      setEmailError(err.message || 'Failed to send reset link');
    }
    setEmailLoading(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 8) {
      setResetError('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match');
      return;
    }

    setResetLoading(true);
    setResetError('');
    setResetSuccess('');

    try {
      const result = await businessService.resetPassword(token, newPassword, confirmPassword);
      if (result.success) {
        setResetSuccess('Password reset successfully! Redirecting to login...');
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        setResetError(result.message || 'Failed to reset password');
      }
    } catch (err) {
      setResetError(err.message || 'Failed to reset password');
    }
    setResetLoading(false);
  };

  const handleBack = () => {
    if (step === 1) {
      navigate('/');
    } else {
      navigate('/');
    }
  };

  return (
    <div className="forgot-password-page">
      <div className="forgot-password-container">
        <Card className="forgot-password-card">
          <Card.Body className="forgot-password-body">
            {/* Header */}
            <div className="forgot-password-header">
              <div className="forgot-password-icon">
                {step === 1 ? <Lock size={28} color="white" /> : <Key size={28} color="white" />}
              </div>
              <div>
                <h1 className="forgot-password-title">
                  {step === 1 ? 'Forgot Password' : 'Reset Password'}
                </h1>
                <p className="forgot-password-subtitle">
                  {step === 1 
                    ? 'Enter your email to receive a password reset link' 
                    : 'Create your new password'}
                </p>
              </div>
            </div>

            {/* Back Button */}
            <button
              type="button"
              className="forgot-password-back"
              onClick={handleBack}
            >
              <ArrowLeft size={16} />
              {step === 1 ? 'Back to Login' : 'Back to Login'}
            </button>

            {/* Step 1: Email */}
            {step === 1 && (
              <Form onSubmit={handleEmailSubmit}>
                {emailError && (
                  <Alert className="forgot-password-error">
                    <XCircle size={18} />
                    {emailError}
                  </Alert>
                )}

                {emailSuccess && (
                  <Alert className="forgot-password-success">
                    <CheckCircle size={18} />
                    {emailSuccess}
                  </Alert>
                )}

                <div className="forgot-password-form-group">
                  <Form.Label className="forgot-password-label">
                    Email Address
                  </Form.Label>
                  <div className="forgot-password-input-wrapper">
                    <Mail size={18} className="forgot-password-input-icon" />
                    <Form.Control
                      type="email"
                      placeholder="Enter your email address"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="forgot-password-input"
                      required
                      disabled={emailLoading}
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={emailLoading}
                  className="forgot-password-submit"
                >
                  {emailLoading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <Send size={18} />
                      Send Reset Link
                    </>
                  )}
                </Button>
              </Form>
            )}

            {/* Step 2: Reset Password */}
            {step === 2 && (
              <Form onSubmit={handleResetPassword}>
                {resetError && (
                  <Alert className="forgot-password-error">
                    <XCircle size={18} />
                    {resetError}
                  </Alert>
                )}

                {resetSuccess && (
                  <Alert className="forgot-password-success">
                    <CheckCircle size={18} />
                    {resetSuccess}
                  </Alert>
                )}

                <div className="forgot-password-form-group">
                  <Form.Label className="forgot-password-label">
                    New Password
                  </Form.Label>
                  <div className="forgot-password-password-wrapper">
                    <Form.Control
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Create new password (min 8 chars)"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="forgot-password-input forgot-password-password-input"
                      required
                      disabled={resetLoading}
                    />
                    <button
                      type="button"
                      className="forgot-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="forgot-password-form-group">
                  <Form.Label className="forgot-password-label">
                    Confirm Password
                  </Form.Label>
                  <div className="forgot-password-password-wrapper">
                    <Form.Control
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm your password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="forgot-password-input forgot-password-password-input"
                      required
                      disabled={resetLoading}
                    />
                    <button
                      type="button"
                      className="forgot-password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    >
                      {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={resetLoading}
                  className="forgot-password-submit"
                >
                  {resetLoading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    <>
                      <Lock size={18} />
                      Reset Password
                    </>
                  )}
                </Button>
              </Form>
            )}

            {/* Footer */}
            <div className="forgot-password-footer">
              <span>
                <Shield size={14} />
                Secure connection
              </span>
              <span>
                <Sparkles size={14} />
                Password recovery
              </span>
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
}